import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Separator } from "./ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useGlobalLoader } from "./GlobalLoader";
import inovaone_api from "@/services/inovaone_api";
import { Slider } from "./ui/slider";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { DataTable } from "./data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { alert } from "@/contexts/AlertModalContext";
import { useLeitores } from "@/contexts/LeitoresContext";
import { useSocket } from "@/contexts/SocketContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { AdminContext } from "@/contexts/AdminContext";
import {
  Activity,
  CircleDot,
  Cpu,
  FileText,
  HelpCircle,
  Loader2,
  Network,
  Play,
  Printer,
  Radio,
  Save,
  Settings2,
  Square,
  Tag,
  Trash2,
  Wifi,
  X,
  Zap,
} from "lucide-react";

type ModuloDeImpressaoContextType = {
  isOpenSheet: boolean;
  toggleSheetModulo: () => void;
  isOpenDrawer: boolean;
  toggleDrawerModulo: () => void;
  toggleLer: () => void;
  estaLendo: boolean;
  etiquetasLidas: TagLida[];
  setEtiquetasLidas: React.Dispatch<React.SetStateAction<TagLida[]>>;
  imprimirEtiquetaTeste: () => void;
  /** Pede status ao módulo (ST). Após ~1.5s sem resposta, mostra warning.
   *  Se a resposta chegar depois do warning, ele é fechado automaticamente. */
  coletarInfoDoModulo: () => void;
  /** True enquanto aguarda resposta — usar pra desabilitar o botão. */
  aguardandoStatusModulo: boolean;
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  infosDoModulo: string[];

  /** Pede leitura de etiqueta ao módulo (LEPR). Após ~1.5s sem resposta,
   *  mostra warning. Quando a resposta chega, abre o modal de conferência. */
  lerEtiquetaParaConferir: () => void;
  /** True enquanto aguarda resposta — usar pra desabilitar o botão. */
  aguardandoLeituraEtiqueta: boolean;
  conferirEtiquetaOpen: boolean;
  setConferirEtiquetaOpen: React.Dispatch<React.SetStateAction<boolean>>;
  epcConferido: string;
};

type TagLida = {
  id: string;
  epc: string;
  leituras: number;
  rssi: number;
};

const ModuloDeImpressaoContext = createContext<
  ModuloDeImpressaoContextType | undefined
>(undefined);

export function ModuloDeImpressaoProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [isOpenSheet, setIsOpenSheet] = useState(false);
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const [estaLendo, setEstaLendo] = useState<boolean>(false);
  const [etiquetasLidas, setEtiquetasLidas] = useState<TagLida[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [infosDoModulo, setInfosDoModulo] = useState<string[]>([]);
  const { lastMessage, enviarMensagem } = useSocket();
  const { impressaoConfig } = useImpressaoConfig();

  // Lock: só processa a 1ª INFO recebida após pedir o status.
  // Qualquer INFO subsequente (módulo enviando duplicado, StrictMode, etc.)
  // é ignorada até a próxima chamada de coletarInfoDoModulo.
  //
  // Mantemos ref + state em paralelo: ref pra checagem síncrona dentro de
  // callbacks (sem stale closure), state pra disparar re-render e
  // desabilitar o botão visualmente.
  const aguardandoRespostaRef = useRef(false);
  const [aguardandoStatusModulo, setAguardandoStatusModulo] = useState(false);
  const timeoutModuloRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setAguardando = (v: boolean) => {
    aguardandoRespostaRef.current = v;
    setAguardandoStatusModulo(v);
  };

  // --- Conferir Etiqueta (LEPR) ---
  const aguardandoLeituraRef = useRef(false);
  const [aguardandoLeituraEtiqueta, setAguardandoLeituraEtiqueta] =
    useState(false);
  const timeoutLeituraRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [conferirEtiquetaOpen, setConferirEtiquetaOpen] = useState(false);
  const [epcConferido, setEpcConferido] = useState<string>("");

  const setAguardandoLeitura = (v: boolean) => {
    aguardandoLeituraRef.current = v;
    setAguardandoLeituraEtiqueta(v);
  };

  const lerEtiquetaParaConferir = () => {
    if (aguardandoLeituraRef.current) return;
    setAguardandoLeitura(true);

    if (timeoutLeituraRef.current) {
      clearTimeout(timeoutLeituraRef.current);
      timeoutLeituraRef.current = null;
    }
    alert.dismissAll();

    enviarMensagem("LEPR");

    // Sem resposta em 1.5s → warning + libera lock pra nova tentativa.
    timeoutLeituraRef.current = setTimeout(() => {
      alert.warning(t("modulo.noTagReadInTime"));
      timeoutLeituraRef.current = null;
      setAguardandoLeitura(false);
    }, 1500);
  };

  const coletarInfoDoModulo = () => {
    // Duplo-click no botão? Ignora se já está aguardando resposta.
    if (aguardandoRespostaRef.current) return;
    setAguardando(true);

    // Cancela timeout e dismissa avisos antigos antes de uma nova tentativa
    if (timeoutModuloRef.current) {
      clearTimeout(timeoutModuloRef.current);
      timeoutModuloRef.current = null;
    }
    alert.dismissAll();

    enviarMensagem("ST");

    // Após 1.5s sem resposta, mostra warning e libera o lock pra nova tentativa.
    // Se a resposta chegar depois, o useEffect [lastMessage] dismissa esse warning.
    timeoutModuloRef.current = setTimeout(() => {
      alert.warning(t("modulo.notDetected"));
      timeoutModuloRef.current = null;
      setAguardando(false);
    }, 1500);
  };
  const imprimirEtiquetaTeste = () => {
    enviarMensagem(
      "+^XA^RFW^LH120,20^FO0,20 ^FO30,20^A0N,30,30^FDETIQUETA DE TESTE^FS  ^FO180,50^A0N,25,25^FD@inovacode^FS^XZ|true|0|0|false",
    );
  };

  useEffect(() => {
    if (!lastMessage || !lastMessage.data) return;

    // Resposta de status do módulo (INFO|...)
    if (lastMessage.data.includes("INFO")) {
      // Só processa se estamos efetivamente aguardando uma resposta de ST.
      // Ignora INFOs espontâneas / duplicadas que o módulo possa mandar.
      if (!aguardandoRespostaRef.current) return;
      setAguardando(false);

      // Cancela timeout pendente e dismissa o warning se já apareceu
      if (timeoutModuloRef.current) {
        clearTimeout(timeoutModuloRef.current);
        timeoutModuloRef.current = null;
      }
      alert.dismissAll();

      const msg = lastMessage.data.split("|");
      setInfosDoModulo(msg);
      setDialogOpen(true);
    }

    // Resposta de leitura de etiqueta (LEPR+<epc>)
    if (lastMessage.data.toLowerCase().includes("lepr+")) {
      // Só processa se estamos aguardando uma leitura solicitada.
      if (!aguardandoLeituraRef.current) return;
      setAguardandoLeitura(false);

      if (timeoutLeituraRef.current) {
        clearTimeout(timeoutLeituraRef.current);
        timeoutLeituraRef.current = null;
      }
      alert.dismissAll();

      const tag = lastMessage.data.split("+")[1]?.trim() || "";
      setEpcConferido(tag);
      setConferirEtiquetaOpen(true);
    }

    const resposta = lastMessage.data.toLowerCase();
    if (resposta.includes("cali+")) {
      // cali+<antena>;<epc>;<rssi>
      const partes = resposta.split(";");
      if (partes.length < 3) {
        console.error("Resposta de calibração inválida:", resposta);
        return;
      }
      const epc = partes[1].trim().toUpperCase();
      const rssi = parseInt(partes[2].trim());
      if (!epc || isNaN(rssi)) {
        console.error("EPC ou RSSI inválidos:", epc, rssi);
        return;
      }

      setEtiquetasLidas((prev) => {
        const idx = prev.findIndex((tag) => tag.epc === epc);
        if (idx !== -1) {
          // Atualiza leituras e rssi da etiqueta existente
          const novaLista = [...prev];
          novaLista[idx] = {
            ...novaLista[idx],
            leituras: novaLista[idx].leituras + 1,
            rssi,
          };
          return novaLista;
        } else {
          // Adiciona nova etiqueta
          return [
            ...prev,
            {
              id: epc,
              epc,
              leituras: 1,
              rssi,
            },
          ];
        }
      });
    }
  }, [lastMessage]);

  const toggleLer = () => {
    setEstaLendo((prev) => !prev);
    if (!estaLendo) {
      enviarMensagem(`P:${impressaoConfig?.potencia_padrao}`);
      enviarMensagem("IL");
    } else {
      setEtiquetasLidas([]);
      enviarMensagem("FL");
    }
  };

  const toggleDrawerModulo = () => {
    setIsOpenDrawer((prev) => !prev);
  };

  const toggleSheetModulo = () => {
    setIsOpenSheet((prev) => !prev);
  };

  useEffect(() => {
    if ((!isOpenDrawer || !isOpenSheet) && estaLendo) {
      toggleLer();
    }
  }, [isOpenSheet, isOpenDrawer]);

  return (
    <ModuloDeImpressaoContext.Provider
      value={{
        isOpenSheet,
        toggleSheetModulo,
        isOpenDrawer,
        toggleDrawerModulo,
        toggleLer,
        estaLendo,
        etiquetasLidas,
        setEtiquetasLidas,
        imprimirEtiquetaTeste,
        coletarInfoDoModulo,
        aguardandoStatusModulo,
        dialogOpen,
        setDialogOpen,
        infosDoModulo,
        lerEtiquetaParaConferir,
        aguardandoLeituraEtiqueta,
        conferirEtiquetaOpen,
        setConferirEtiquetaOpen,
        epcConferido,
      }}
    >
      {children}
      <ModuloDeImpressao />
    </ModuloDeImpressaoContext.Provider>
  );
}

export function useModuloDeImpressao() {
  const context = useContext(ModuloDeImpressaoContext);
  if (!context) {
    throw new Error(
      "useModuloDeImpressao deve ser usado dentro de ModuloDeImpressaoProvider",
    );
  }
  return context;
}

function ModuloDeImpressao() {
  const { t } = useTranslation();
  const context = useContext(ModuloDeImpressaoContext);
  if (!context) return null;
  const {
    isOpenSheet,
    toggleSheetModulo,
    isOpenDrawer,
    toggleDrawerModulo,
    estaLendo,
    toggleLer,
    etiquetasLidas,
    setEtiquetasLidas,
    imprimirEtiquetaTeste,
    coletarInfoDoModulo,
    aguardandoStatusModulo,
    dialogOpen,
    setDialogOpen,
    infosDoModulo,
  } = context;
  const { impressaoConfig, getImpressaoConfig } = useImpressaoConfig();
  const { setIsLoading, isLoading } = useGlobalLoader();
  const [hardwareId, setHardwareId] = useState<string>("");
  const [hardwareId2, setHardwareId2] = useState<string>("");
  const [forcaPadrao, setForcaPadrao] = useState<number>(2);
  const [atrasoDeGravacao, setAtrasoDeGravacao] = useState<number>(720);
  const [tentativasDeGravacao, setTentativasDeGravacao] = useState<number>(2);
  const { leitores } = useLeitores();
  const { isAdmin } = useContext(AdminContext)!;

  const atualizarConfiguracoesDoModulo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!impressaoConfig) {
      console.error("Configuração de impressão não encontrada");
      alert.error(t("modulo.configNotFound"));
      return;
    }
    setIsLoading(true);
    try {
      await inovaone_api.post(`/impressao_config`, [
        {
          ...impressaoConfig,
          app_id: hardwareId,
          hardwareid_alternativo: hardwareId2,
          potencia_padrao: forcaPadrao,
          tempo_delay_gravacao: atrasoDeGravacao,
          tentativas_gravacao: tentativasDeGravacao,
        },
      ]);
      await getImpressaoConfig();
      alert.success(t("modulo.configUpdated"));
    } catch (error) {
      console.error(
        "Erro ao atualizar configurações do módulo de impressão:",
        error,
      );
      alert.error(t("modulo.configUpdateError"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (impressaoConfig) {
      setHardwareId(impressaoConfig.app_id || "");
      setHardwareId2(impressaoConfig.hardwareid_alternativo || "");
      setForcaPadrao(parseInt(impressaoConfig?.potencia_padrao || "2"));
      setAtrasoDeGravacao(
        parseInt(impressaoConfig?.tempo_delay_gravacao || "720"),
      );
      setTentativasDeGravacao(
        parseInt(impressaoConfig?.tentativas_gravacao?.toString() || "2"),
      );
    }
  }, [impressaoConfig, isOpenSheet]);

  // ---- Calibração: helpers ----
  const rssiPercent = (rssi: number) => {
    // RSSI tipicamente vai de -100 (fraco) a -30 (forte)
    const clamped = Math.max(-100, Math.min(-30, rssi));
    return Math.round(((clamped + 100) / 70) * 100);
  };
  const rssiColor = (rssi: number) => {
    const p = rssiPercent(rssi);
    if (p >= 70) return "bg-emerald-500";
    if (p >= 40) return "bg-amber-500";
    return "bg-red-500";
  };

  const columns: ColumnDef<TagLida>[] = [
    {
      accessorKey: "epc",
      header: "EPC",
      cell: ({ row }) => (
        <span className="text-xs">{row.original.epc}</span>
      ),
    },
    {
      accessorKey: "leituras",
      header: () => <div className="text-center">{t("modulo.readings")}</div>,
      cell: ({ row }) => (
        <div className="text-center">
          <Badge variant="secondary" className="font-medium">
            {row.original.leituras}
          </Badge>
        </div>
      ),
    },
    {
      accessorKey: "rssi",
      header: "RSSI",
      cell: ({ row }) => {
        const r = row.original.rssi;
        return (
          <div className="flex items-center gap-2 min-w-[140px]">
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full ${rssiColor(r)} transition-all`}
                style={{ width: `${rssiPercent(r)}%` }}
              />
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums w-10 text-right">
              {r} dBm
            </span>
          </div>
        );
      },
    },
  ];

  // ---- Dados do módulo: preparação ----
  const dadosModulo = {
    rede: infosDoModulo[1]?.trim() || "inovacode",
    hardwareId: infosDoModulo[4] ?? "",
    versao: infosDoModulo[5] ?? "",
    macWifi: infosDoModulo[6]?.replace("WFMAC>", "") ?? "",
    rssiWifi: infosDoModulo[10]?.replace("RSSI>", "") ?? "",
    disponivel: infosDoModulo[7] ?? "",
  };
  const moduloDisponivel = dadosModulo.disponivel.trim().toUpperCase() === "SIM";

  const leitor1 = leitores.find((l) => l.serial === hardwareId);
  const leitor2 = leitores.find((l) => l.serial === hardwareId2);

  return (
    <div>
      {/* Dialog: Dados do Módulo */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Cpu className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-base">
                  {t("modulo.dataTitle")}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {t("modulo.dataDesc")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Status compacto */}
          <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 p-3">
            <span className="text-xs text-muted-foreground">
              {t("modulo.status")}
            </span>
            <Badge
              variant="outline"
              className={
                moduloDisponivel
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 gap-1"
                  : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20 gap-1"
              }
            >
              <CircleDot className="h-3 w-3" />
              {moduloDisponivel ? t("modulo.available") : t("modulo.busy")}
            </Badge>
          </div>

          {/* Hardware */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              <Cpu className="h-3 w-3" />
              {t("modulo.hardware")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label
                  htmlFor="modulo-info-hardware-id"
                  className="text-xs text-muted-foreground"
                >
                  {t("modulo.hardwareId")}
                </Label>
                <Input
                  disabled
                  id="modulo-info-hardware-id"
                  value={dadosModulo.hardwareId || "—"}
                />
              </div>
              <div className="grid gap-1.5">
                <Label
                  htmlFor="modulo-info-versao"
                  className="text-xs text-muted-foreground"
                >
                  {t("modulo.firmwareVersion")}
                </Label>
                <Input
                  disabled
                  id="modulo-info-versao"
                  value={dadosModulo.versao || "—"}
                />
              </div>
            </div>
          </div>

          {/* Conexão */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              <Wifi className="h-3 w-3" />
              {t("modulo.wifiConnection")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label
                  htmlFor="modulo-info-rede"
                  className="text-xs text-muted-foreground"
                >
                  {t("modulo.network")}
                </Label>
                <Input
                  disabled
                  id="modulo-info-rede"
                  value={dadosModulo.rede || "—"}
                />
              </div>
              <div className="grid gap-1.5">
                <Label
                  htmlFor="modulo-info-rssi"
                  className="text-xs text-muted-foreground"
                >
                  {t("modulo.signalRssi")}
                </Label>
                <Input
                  disabled
                  id="modulo-info-rssi"
                  value={dadosModulo.rssiWifi || "—"}
                />
              </div>
              <div className="grid gap-1.5 col-span-2">
                <Label
                  htmlFor="modulo-info-mac"
                  className="text-xs text-muted-foreground"
                >
                  {t("modulo.wifiMac")}
                </Label>
                <Input
                  disabled
                  id="modulo-info-mac"
                  value={dadosModulo.macWifi || "—"}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              <X className="h-4 w-4" />
              {t("modulo.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sheet: Configurações + Drawer de Calibração */}
      <Sheet open={isOpenSheet} onOpenChange={toggleSheetModulo}>
        <SheetContent className="overflow-y-auto z-[3333] sm:max-w-md flex flex-col gap-0 p-0">
          {/* Drawer: Calibração (fica em z-index acima do Sheet) */}
          <Drawer
            open={isOpenDrawer}
            onOpenChange={toggleDrawerModulo}
            dismissible={false}
          >
            <DrawerContent className="z-[9999]">
              <DrawerHeader>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Radio className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <DrawerTitle className="text-base">
                      {t("modulo.calibrationTitle")}
                    </DrawerTitle>
                    <DrawerDescription className="text-xs">
                      {t("modulo.calibrationDesc")}
                    </DrawerDescription>
                  </div>
                  {estaLendo && (
                    <Badge
                      variant="outline"
                      className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20 gap-1 animate-pulse"
                    >
                      <CircleDot className="h-3 w-3" />
                      {t("modulo.reading")}
                    </Badge>
                  )}
                </div>
              </DrawerHeader>

              <div className="px-4 pb-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {t("modulo.detectedTags")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="font-medium">
                      {etiquetasLidas.length}{" "}
                      {etiquetasLidas.length === 1
                        ? t("modulo.tag")
                        : t("modulo.tags")}
                    </Badge>
                    {etiquetasLidas.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setEtiquetasLidas([])}
                        disabled={estaLendo}
                      >
                        <Trash2 className="h-3 w-3" />
                        {t("modulo.clear")}
                      </Button>
                    )}
                  </div>
                </div>
                {etiquetasLidas.length === 0 ? (
                  <div className="rounded-md border border-dashed p-6 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Tag className="h-8 w-8 opacity-40" />
                    <p className="text-xs text-center">
                      {estaLendo
                        ? t("modulo.bringTagClose")
                        : t("modulo.noTagDetected")}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-md border max-h-72 overflow-y-auto">
                    <DataTable columns={columns} data={etiquetasLidas} />
                  </div>
                )}
              </div>

              <DrawerFooter>
                <Button
                  disabled={isLoading}
                  variant={estaLendo ? "destructive" : "default"}
                  onClick={toggleLer}
                  className="w-full"
                >
                  {estaLendo ? (
                    <>
                      <Square className="h-4 w-4" />
                      {t("modulo.stopReading")}
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      {t("modulo.startReading")}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={toggleDrawerModulo}
                  disabled={isLoading || estaLendo}
                  className="w-full"
                  title={estaLendo ? t("modulo.stopBeforeClosing") : ""}
                >
                  <X className="h-4 w-4" />
                  {t("modulo.close")}
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>

          <SheetHeader className="border-b">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Settings2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-base">
                  {t("modulo.settingsTitle")}
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {t("modulo.settingsDesc")}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <TooltipProvider delayDuration={250}>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Identificação */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
                      1
                    </div>
                    <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                      <Network className="h-4 w-4 text-muted-foreground" />
                      {t("modulo.identification")}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid gap-1.5">
                    <Label
                      htmlFor="modulo-sheet-hardware-id"
                      className="text-xs text-muted-foreground"
                    >
                      {t("modulo.hardwareId1")}
                    </Label>
                    <Input
                      disabled={isLoading || !isAdmin}
                      id="modulo-sheet-hardware-id"
                      value={hardwareId}
                      onChange={(e) => setHardwareId(e.target.value)}
                      placeholder={t("modulo.readerSerial")}
                    />
                    {leitor1 && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <CircleDot className="h-2.5 w-2.5 text-emerald-500" />
                        {leitor1.nm_leitor}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-1.5">
                    <Label
                      htmlFor="modulo-sheet-hardware-id-2"
                      className="text-xs text-muted-foreground"
                    >
                      {t("modulo.hardwareId2")}{" "}
                      <span className="text-[10px]">
                        {t("modulo.alternative")}
                      </span>
                    </Label>
                    <Input
                      disabled={isLoading || !isAdmin}
                      id="modulo-sheet-hardware-id-2"
                      value={hardwareId2}
                      onChange={(e) => setHardwareId2(e.target.value)}
                      placeholder={t("modulo.optional")}
                    />
                    {leitor2 && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <CircleDot className="h-2.5 w-2.5 text-emerald-500" />
                        {leitor2.nm_leitor}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* RF e gravação */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
                      2
                    </div>
                    <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                      <Zap className="h-4 w-4 text-muted-foreground" />
                      {t("modulo.rfAndWriting")}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="modulo-sheet-forca-padrao"
                        className="text-xs text-muted-foreground flex items-center gap-1"
                      >
                        {t("modulo.rfPower")}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3 w-3" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {t("modulo.rfPowerTooltip")}
                          </TooltipContent>
                        </Tooltip>
                      </Label>
                      <Badge variant="secondary" className="font-medium">
                        {forcaPadrao} / 26
                      </Badge>
                    </div>
                    <Slider
                      disabled={isLoading || !isAdmin}
                      id="modulo-sheet-forca-padrao"
                      value={[forcaPadrao]}
                      min={1}
                      max={26}
                      step={1}
                      onValueChange={(value) => setForcaPadrao(value[0])}
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>{t("modulo.min")} (1)</span>
                      <span>{t("modulo.max")} (26)</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5">
                      <Label
                        htmlFor="modulo-sheet-atraso-de-gravacao"
                        className="text-xs text-muted-foreground"
                      >
                        {t("modulo.delayMs")}
                      </Label>
                      <Input
                        disabled={isLoading || !isAdmin}
                        id="modulo-sheet-atraso-de-gravacao"
                        type="number"
                        min={0}
                        value={Number.isFinite(atrasoDeGravacao) ? atrasoDeGravacao : 0}
                        onChange={(e) => {
                          const v = parseInt(e.target.value);
                          setAtrasoDeGravacao(Number.isFinite(v) ? v : 0);
                        }}
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label
                        htmlFor="modulo-sheet-tentativas-de-gravacao"
                        className="text-xs text-muted-foreground"
                      >
                        {t("modulo.attempts")}
                      </Label>
                      <Input
                        disabled={isLoading || !isAdmin}
                        id="modulo-sheet-tentativas-de-gravacao"
                        type="number"
                        min={1}
                        value={Number.isFinite(tentativasDeGravacao) ? tentativasDeGravacao : 1}
                        onChange={(e) => {
                          const v = parseInt(e.target.value);
                          setTentativasDeGravacao(Number.isFinite(v) ? v : 1);
                        }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ações rápidas */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-semibold">
                      3
                    </div>
                    <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                      <Activity className="h-4 w-4 text-muted-foreground" />
                      {t("modulo.quickActions")}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={coletarInfoDoModulo}
                    disabled={isLoading || aguardandoStatusModulo}
                  >
                    {aguardandoStatusModulo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                    {aguardandoStatusModulo
                      ? t("modulo.waitingResponse")
                      : t("modulo.collectModuleInfo")}
                  </Button>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={toggleDrawerModulo}
                      disabled={isLoading}
                    >
                      <Radio className="h-4 w-4" />
                      {t("modulo.calibrate")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={imprimirEtiquetaTeste}
                      disabled={isLoading}
                    >
                      <Printer className="h-4 w-4" />
                      {t("modulo.test")}
                    </Button>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      if (!impressaoConfig?.app_id) {
                        alert.error(t("modulo.defineHardwareIdForRoll"));
                        return;
                      }
                      const base =
                        window.location.hostname === "localhost"
                          ? `${window.location.origin}/ajustar-rolo`
                          : "https://ws-homolog.inovacode.app.br/v1/ajustar-rolo";
                      window.open(
                        `${base}/${impressaoConfig?.app_id}/${impressaoConfig?._id}`,
                        "_blank",
                      );
                    }}
                  >
                    <Settings2 className="h-4 w-4" />
                    {t("modulo.adjustRoll")}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TooltipProvider>

          <SheetFooter className="border-t flex-row gap-2 px-4 py-3">
            <SheetClose asChild disabled={isLoading}>
              <Button variant="outline" className="flex-1">
                <X className="h-4 w-4" />
                {t("modulo.close")}
              </Button>
            </SheetClose>
            <Button
              disabled={isLoading || !isAdmin}
              onClick={atualizarConfiguracoesDoModulo}
              className="flex-1"
              title={!isAdmin ? t("modulo.onlyAdminsCanSave") : ""}
            >
              <Save className="h-4 w-4" />
              {t("modulo.save")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
